import { type FC, useState, useEffect, useMemo } from 'react';
import { useAppStore } from '@/stores/app-store';
import { fetchWindyWebcams, HAS_WINDY, type WindyWebcam } from '@/services/windy-webcams';
import { PUBLIC_CAMS } from '@/config/webcam-sources';

// ---------------------------------------------------------------------------
// Live Cam Panel — Windy Webcam API (primary) / curated fallback
// Windy embed URLs work natively in iframes — no YouTube restrictions
// ---------------------------------------------------------------------------

// ── Continent filter ────────────────────────────────────────────────────────
const CONTINENTS: { id: string; label: string }[] = [
  { id: 'all',           label: 'All' },
  { id: 'Asia',          label: 'Asia' },
  { id: 'Europe',        label: 'Europe' },
  { id: 'North America', label: 'Americas' },
  { id: 'South America', label: 'S.Am' },
  { id: 'Africa',        label: 'Africa' },
  { id: 'Oceania',       label: 'Oceania' },
];

// ── Windy embed player ──────────────────────────────────────────────────────
const WindyPlayer: FC<{ cam: WindyWebcam }> = ({ cam }) => (
  <iframe
    key={cam.webcamId}
    src={cam.player.day.embed}
    className="absolute inset-0 w-full h-full"
    allow="autoplay; fullscreen"
    allowFullScreen
    loading="lazy"
  />
);

// ── Fallback card (no Windy key or no embed URL) ────────────────────────────
const FallbackCard: FC<{ cam: WindyWebcam }> = ({ cam }) => (
  <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900/90 gap-2">
    {cam.images?.current?.preview && (
      <img
        src={cam.images.current.preview}
        alt={cam.title}
        className="absolute inset-0 w-full h-full object-cover opacity-50"
      />
    )}
    <div className="relative z-10 flex flex-col items-center gap-1 px-2 text-center">
      <div className="text-[13px] font-bold text-slate-100 drop-shadow">{cam.title}</div>
      <div className="text-[10px] text-slate-300">{cam.location.city}, {cam.location.country}</div>
      {cam.urls.website && (
        <a
          href={cam.urls.website}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1 px-4 py-1.5 text-[11px] font-medium bg-cyan-900/60 hover:bg-cyan-800/80 text-cyan-300 rounded-full transition-colors border border-cyan-700/60"
        >
          Watch Live ↗
        </a>
      )}
    </div>
  </div>
);

// ── Thumbnail grid button ───────────────────────────────────────────────────
const CamThumb: FC<{ cam: WindyWebcam; active: boolean; onClick: () => void }> = ({ cam, active, onClick }) => (
  <button
    onClick={onClick}
    className={`relative flex flex-col items-center gap-0.5 rounded text-center transition-all overflow-hidden ${
      active ? 'ring-1 ring-cyan-600/70' : 'hover:bg-white/5'
    }`}
  >
    {cam.images?.current?.thumbnail ? (
      <img
        src={cam.images.current.thumbnail}
        alt={cam.title}
        className="w-full aspect-video object-cover rounded"
        loading="lazy"
      />
    ) : (
      <div className="w-full aspect-video bg-slate-800 rounded flex items-center justify-center text-base">📷</div>
    )}
    <span className={`text-[8px] font-medium leading-tight truncate w-full px-0.5 ${
      active ? 'text-cyan-300' : 'text-slate-400'
    }`}>
      {cam.location.city || cam.title}
    </span>
  </button>
);

// ── Adapt PUBLIC_CAMS fallback to WindyWebcam shape ─────────────────────────
function publicCamToWindy(c: typeof PUBLIC_CAMS[number]): WindyWebcam {
  return {
    webcamId: c.id,
    title: c.name,
    status: 'active',
    location: { city: c.city, region: '', country: c.country, continent: c.region, latitude: c.lat, longitude: c.lon, timezone: '' },
    images: { current: { thumbnail: '', preview: '' }, daylight: true },
    player: {
      day: {
        embed: c.videoId
          ? `https://www.youtube.com/embed/${c.videoId}?autoplay=1&mute=1&controls=1&modestbranding=1&rel=0`
          : '',
        web: c.websiteUrl ?? '',
      },
    },
    urls: { website: c.websiteUrl ?? '' },
  };
}

// ── Main panel ──────────────────────────────────────────────────────────────
export const LiveCamPanel: FC = () => {
  const windyWebcams = useAppStore((s) => s.windyWebcams);
  const setWindyWebcams = useAppStore((s) => s.setWindyWebcams);
  const [loading, setLoading] = useState(false);
  const [continent, setContinent] = useState('all');
  const [activeCam, setActiveCam] = useState<WindyWebcam | null>(null);

  // Fetch from Windy on first mount
  useEffect(() => {
    if (!HAS_WINDY || windyWebcams.length > 0) return;
    setLoading(true);
    fetchWindyWebcams()
      .then(setWindyWebcams)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isWindy = HAS_WINDY && windyWebcams.length > 0;

  const allCams: WindyWebcam[] = useMemo(
    () => (windyWebcams.length > 0 ? windyWebcams : PUBLIC_CAMS.map(publicCamToWindy)),
    [windyWebcams],
  );

  const filtered = useMemo(
    () => (continent === 'all' ? allCams : allCams.filter((c) => c.location.continent === continent)),
    [allCams, continent],
  );

  const currentCam = activeCam ?? filtered[0] ?? null;

  // Reset selection when filtered list changes and current cam is no longer in it
  useEffect(() => {
    if (activeCam && !filtered.find((c) => c.webcamId === activeCam.webcamId)) {
      setActiveCam(null);
    }
  }, [filtered, activeCam]);

  return (
    <>
      {/* Header */}
      <div className="panel-header flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="panel-title">Live Cams</span>
          <span className="pulse-dot" />
          <span className="text-[10px] text-cyan-400 font-medium uppercase tracking-wider">
            {isWindy ? 'Windy API' : 'Global'}
          </span>
        </div>
        <span className="text-[10px] text-slate-500">
          {loading ? 'Loading…' : `${allCams.length} cameras`}
        </span>
      </div>

      {/* Video Player */}
      {currentCam && (
        <div className="relative w-full h-[200px] bg-black rounded overflow-hidden">
          {currentCam.player.day.embed ? (
            <WindyPlayer cam={currentCam} />
          ) : (
            <FallbackCard cam={currentCam} />
          )}
          {/* Location badge */}
          <div className="absolute top-1.5 left-1.5 flex items-center gap-1.5 bg-black/70 backdrop-blur-sm rounded px-2 py-0.5 pointer-events-none z-10">
            <span className="text-[10px] font-bold text-white truncate max-w-[130px]">
              {currentCam.location.city || currentCam.title}
            </span>
            <span className="text-[8px] text-slate-400">{currentCam.location.country}</span>
          </div>
          {/* Source badge */}
          <div className="absolute top-1.5 right-1.5 bg-black/70 backdrop-blur-sm rounded px-1.5 py-0.5 pointer-events-none z-10">
            <span className={`text-[8px] font-bold ${isWindy ? 'text-cyan-400' : 'text-red-400'}`}>
              {isWindy ? 'WINDY' : 'YT'}
            </span>
          </div>
        </div>
      )}

      {/* Active cam info */}
      {currentCam && (
        <div className="px-2 py-1 border-b border-slate-800/40">
          <div className="text-[11px] font-semibold text-slate-200 truncate">{currentCam.title}</div>
          <div className="text-[9px] text-slate-500">
            {currentCam.location.city}{currentCam.location.region ? `, ${currentCam.location.region}` : ''} · {currentCam.location.country}
          </div>
        </div>
      )}

      {/* Continent Filter */}
      <div className="flex items-center gap-0.5 px-1 pt-1 flex-wrap">
        {CONTINENTS.map((c) => (
          <button
            key={c.id}
            onClick={() => setContinent(c.id)}
            className={`px-1.5 py-0.5 text-[9px] rounded transition-colors ${
              continent === c.id ? 'bg-cyan-900/40 text-cyan-300' : 'text-slate-500 hover:text-slate-300'
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Camera Thumbnail Grid */}
      <div className="panel-body grid grid-cols-4 gap-1 max-h-32 overflow-y-auto pt-1">
        {filtered.slice(0, 40).map((cam) => (
          <CamThumb
            key={cam.webcamId}
            cam={cam}
            active={currentCam?.webcamId === cam.webcamId}
            onClick={() => setActiveCam(cam)}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 px-2 py-1 border-t border-slate-800/40">
        {isWindy ? (
          <span className="text-[8px] text-slate-600">
            <span className="text-cyan-500">●</span> Windy embedded stream
          </span>
        ) : (
          <>
            <span className="flex items-center gap-1 text-[8px] text-slate-600">
              <span className="text-red-400">●</span> YouTube (may be blocked)
            </span>
            <span className="text-[8px] text-amber-700">Set VITE_WINDY_API_KEY for live streams</span>
          </>
        )}
        <span className="ml-auto text-[8px] text-slate-600">{filtered.length} shown</span>
      </div>
    </>
  );
};
